import { useCompositionContext } from '@src/components/sidepanel/CompositionContext';
import { err, ok } from '@src/services/common/Result';
import {
  buildUploadedMediaCardTemplate,
  resolveUploadedMediaCardId,
  uploadedMediaCardName,
  uploadedMediaCardTypeFor,
} from '@src/services/videoTemplate/uploadedMediaCard';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { getAudioDurationFromUrl } from '@src/utility/audio';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useCallback } from 'react';

import type { CompositionTemplate } from './types';
import type { Result } from '@src/services/common/Result';
import type { UploadedMediaCardType } from '@src/services/videoTemplate/uploadedMediaCard';
import type { Artifact } from '@src/types.ts/story';

const logger = LogManager.getLogger('hook:useSaveUploadedMediaCard');

export interface SavedUploadedMediaCard {
  /** The saved workspace template, or the generated card when not saved. */
  template: CompositionTemplate;
  /**
   * The generated card's id. It resolves anywhere at once — unlike a new
   * record's id, which the slot's lookup may not hold until the next render —
   * so a slot replace applies the card by it.
   */
  cardId: string;
}

export interface SaveUploadedMediaCardInput {
  artifact: Artifact;
  cardType: UploadedMediaCardType;
  /**
   * Where to file the template. The Graphics panel knows the grid it was
   * uploaded from; the Replace modal leaves it out and the first workspace
   * category holding that kind of card is used.
   */
  target?: { categoryId: string; subcategoryId: string };
}

/**
 * PLAT-11732 — turns an uploaded image or video into an intro / end card and
 * saves it as a workspace template, so it is offered again — in the Graphics
 * grid and the Replace modal, to everyone in the workspace — like any design.
 *
 * Falls back to a card that lives only on this video when it cannot be saved
 * (no workspace, no intro / outro category, or the save fails): the creator
 * still gets their card, it just isn't offered again.
 */
export function useSaveUploadedMediaCard() {
  const videoCreator = useVideoCreatorStore();
  const { categories, addUpdateFavoriteCompositionTemplate } =
    useCompositionContext();

  return useCallback(
    async ({
      artifact,
      cardType,
      target,
    }: SaveUploadedMediaCardInput): Promise<Result<SavedUploadedMediaCard>> => {
      const mediaType = artifact.responsiveImage ? 'image' : 'video';
      const idResult = await resolveUploadedMediaCardId(
        { cardType, mediaType, url: artifact.url },
        getAudioDurationFromUrl,
      );
      const card = idResult.ok
        ? buildUploadedMediaCardTemplate(idResult.value)
        : undefined;
      if (!card) {
        return idResult.ok
          ? err('UNKNOWN', 'Could not build the card')
          : idResult;
      }

      const destination = target ?? findCardCategory(categories, cardType);
      if (!destination || !videoCreator.organization) {
        logger.info('Uploaded card not saved as a template', {
          hasCategory: !!destination,
          hasWorkspace: !!videoCreator.organization,
        });
        return ok({ template: card, cardId: card.id });
      }

      const name = uploadedMediaCardName(cardType);
      try {
        const saved = await addUpdateFavoriteCompositionTemplate(
          {
            ...card,
            // no parent: the card is made from a file, not copied from one
            id: '',
            // Dato requires a thumbnail, so the upload stands in — a video too
            // (the field takes any file) — until the rendered PNG replaces it
            // after save. Without one the create fails for a video.
            thumbnail: { id: artifact.id, url: artifact.url },
          },
          name,
          card.controls,
          destination.categoryId,
          destination.subcategoryId,
          'create',
        );
        if (saved)
          return ok({ template: { ...card, ...saved }, cardId: card.id });
      } catch (error) {
        logger.warn('Saving the uploaded card as a template failed', {
          mediaType,
          ...extractErrorInfo(error),
        });
      }
      return ok({ template: card, cardId: card.id });
    },
    [categories, addUpdateFavoriteCompositionTemplate, videoCreator],
  );
}

/** The first category whose designs are mostly this kind of card. */
function findCardCategory(
  categories: ReturnType<typeof useCompositionContext>['categories'],
  cardType: UploadedMediaCardType,
): { categoryId: string; subcategoryId: string } | undefined {
  for (const category of categories) {
    for (const subcategory of category.subcategories) {
      if (uploadedMediaCardTypeFor(subcategory.compositions) === cardType) {
        return { categoryId: category.id, subcategoryId: subcategory.id };
      }
    }
  }
  return undefined;
}
